
import Vue, { PropType } from 'vue';
import HigheredLogo from '~/assets/img/HigheredLogo.svg';

import WelcomeStartPage from './WelcomeStartPage.vue';
import WelcomeStep1Page from './WelcomeStep1Page.vue';
import WelcomeStep2Page from './WelcomeStep2Page.vue';
import WelcomeStep3Page from './WelcomeStep3Page.vue';
import WelcomeFinalPage from './WelcomeFinalPage.vue';

import { CandidateProfileScorer } from '~/utils/candidate/CandidateProfileScorer';
import { commonProps } from '~/utils/candidate';
import { Candidate } from '~/services/types/candidate';
import WelcomeErrorPage from './WelcomeErrorPage.vue';

enum WelcomePage {
    StartPage = 'StartPage',
    Step1Page = 'Step1Page',
    Step2Page = 'Step2Page',
    Step3Page = 'Step3Page',
    FinalPage = 'FinalPage',
    ErrorPage = 'ErrorPage',
}

export default Vue.extend({
    name: 'Welcome',
    components: {
        WelcomeStartPage,
        WelcomeStep1Page,
        WelcomeStep2Page,
        WelcomeStep3Page,
        WelcomeFinalPage,
        WelcomeErrorPage,
    },
    props: {
        scorer: {
            type: Object as PropType<CandidateProfileScorer>,
            required: true,
        },
        flowStarted: {
            type: Function,
            default: () => {},
        },
        flowCompleted: {
            type: Function,
            default: () => {},
        },

        ...commonProps(),
    },
    data() {
        return {
            WelcomePage,

            currentPage: WelcomePage.StartPage,

            isNewUser: null as boolean | null,

            HigheredLogo,

            availablePages: [] as WelcomePage[],
        };
    },
    computed: {
        isStartStepNeeded(): boolean {
            return this.scorer.getWelcomeFlowStep0Score().progress < 100;
        },
        tryGetNextPage(): WelcomePage | null {
            const idx = this.availablePages.findIndex(p => p === this.currentPage);
            return idx >= 0 && idx < this.availablePages.length - 1
                ? this.availablePages[idx + 1]
                : null;
        },
        tryGetPreviousPage(): WelcomePage | null {
            const idx = this.availablePages.findIndex(p => p === this.currentPage);
            return idx >= 1 ? this.availablePages[idx - 1] : null;
        },
        getPageNumber(): number {
            return this.availablePages.findIndex(p => p === this.currentPage);
        },
        getPageCount(): number {
            return this.availablePages.reduce(
                (acc, p) =>
                    p === WelcomePage.Step1Page ||
                    p === WelcomePage.Step2Page ||
                    p === WelcomePage.Step3Page
                        ? acc + 1
                        : acc,
                0
            );
        },
        hasData(): boolean {
            return Boolean(
                this.user && this.candidate && this.workAuthorizations && this.isNewUser !== null
            );
        },
    },
    methods: {
        calculateIsNewUser() {
            if (this.isNewUser === null)
                if (this.scorer && this.candidate) {
                    const oneDayAgo = new Date(new Date().getTime() - 24 * 60 * 60 * 1000);
                    this.isNewUser =
                        this.scorer?.getNewUserScore().progress === 0 &&
                        this.candidate.createdAt > oneDayAgo;
                }

            return this.isNewUser;
        },
        goToNextPage() {
            var nextPage = this.tryGetNextPage;
            if (nextPage) {
                this.currentPage = nextPage;
            }
        },
        goToPreviousPage() {
            var previousPage = this.tryGetPreviousPage;
            if (previousPage) {
                this.currentPage = previousPage;
            }
        },
        handleFlowCompleted() {
            this.$emit('flowCompleted');
        },
        onError(err: Error) {
            console.error(err);
            this.currentPage = WelcomePage.ErrorPage;
        },
        isStep1Needed(): boolean {
            return this.scorer.getWelcomeFlowStep1Score().progress < 100;
        },
        isStep2Needed(): boolean {
            return this.scorer.getWelcomeFlowStep2Score().progress < 100;
        },
        isStep3Needed(): boolean {
            return this.scorer.getWelcomeFlowStep3Score().progress < 100;
        },
        calculateAvailablePages() {
            let pages = [WelcomePage.StartPage];
            if (this.isStep1Needed()) pages.push(WelcomePage.Step1Page);
            if (this.isStep2Needed()) pages.push(WelcomePage.Step2Page);
            if (this.isStep3Needed()) pages.push(WelcomePage.Step3Page);
            pages.push(WelcomePage.FinalPage);
            return pages;
        },
    },
    watch: {
        scorer: {
            immediate: true,
            handler(scorer: CandidateProfileScorer | null) {
                this.calculateIsNewUser();
            },
        },
        candidate: {
            immediate: true,
            handler(candidate: Candidate | null) {
                this.calculateIsNewUser();
            },
        },
        hasData: {
            immediate: true,
            handler(hasData: boolean) {
                if (hasData) {
                    this.availablePages = this.calculateAvailablePages();
                }
            },
        },
    },
    mounted() {
        this.$emit('flowStarted');
    },
});
